import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { connectToHubUser } from "src/redux/slices/hub";
import {
  readAdminNotification,
  showModalNotificationStartup,
} from "src/redux/slices/notifications";

// Hook que se suscribe al UserHub para escuchar cambios de las
// SystemNotification del admin (`system-notification-changed`).
//
// Al recibir un evento:
//   1. Refetch de las notificaciones del user (`readAdminNotification`).
//   2. Si tras el refetch hay al menos una mandatoria sin leer y esta
//      sesión es del owner (no empleado web) → dispara la apertura del
//      modal bloqueante (`showModalNotificationStartup(true)`).
//
// Dedupe: cada evento trae `notificationId + action + at`. Guardamos
// una firma en un ref para ignorar duplicados que puedan llegar por
// reconexión SignalR.
//
// Sesión de empleado web: NO abrimos modal ni refetcheamos — las
// notificaciones de plataforma son solo para el owner.
export default function useSystemNotificationHub() {
  const dispatch = useDispatch();
  const { user, claims } = useSelector((s) => s.authJwt);
  const seenRef = useRef(new Set());

  const restauranteId = user?.id;
  // Solo owner: si el JWT trae UserEmployeeId, es un empleado logueado
  // vía web/QR — no le concierne esto.
  const isEmployeeWebSession = !!claims?.userEmployeeId;

  useEffect(() => {
    if (!restauranteId || isEmployeeWebSession) return undefined;

    const handler = async (payload) => {
      if (!payload) return;
      const sig = `${payload.notificationId}:${payload.action}:${payload.at}`;
      if (seenRef.current.has(sig)) return;
      seenRef.current.add(sig);
      // Cap del set — evita crecer indefinidamente durante sesiones largas.
      if (seenRef.current.size > 200) {
        const first = seenRef.current.values().next().value;
        seenRef.current.delete(first);
      }

      // Refetch → llena adminNotifications con el estado más reciente
      // (incluye Type, IsMandatory, PublishAt, etc.). Después de esperar,
      // decidimos si abrir el modal.
      try {
        await dispatch(readAdminNotification());
      } catch { /* fetch fail = no-op, se reintentará al F5 */ }

      // Abrimos el modal solo si la acción fue "created" / "updated" /
      // "published" — para "deleted" no forzamos apertura (el listado
      // se actualizará y el badge de la campana refleja el cambio).
      if (payload.action === "deleted") return;
      dispatch(showModalNotificationStartup(true));
    };

    const cleanup = CreateConnection(
      buildHubUrl("/hub/userhub", { restauranteId }),
      [{ id: "system-notification-changed", operator: handler }],
      dispatch,
      connectToHubUser,
      "user",
    );

    return cleanup;
  }, [restauranteId, isEmployeeWebSession, dispatch]);
}
